import { SliderModels } from '@/types/slider'
import PptPage, { PageHeader } from '../PptPage/PptPage'
import { commonUtils } from '@/helpers/commonUtils'
import styles from './PptPage6.module.less'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import VideoScoreBarChart from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoScoreBarChart/VideoScoreBarChart'
import { useScoreChartData } from '@/pages/ProjectDetailPage/components/VideoAnalysis/VideoAnalysis'
import html2canvas from 'html2canvas'

interface Props {
  project: ProjectModels.ProjectDetail
  no: number
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
}

function PptPage6({ project, no, onSliderReady }: Props) {
  const { t } = useTranslation()
  const { firstScoreChart, secondScoreChart } = useScoreChartData(project)
  const mode = project.globalMetrics.overall.score >= project.globalMetrics.overall.meanScore ? 'high' : 'low'
  const [basicSlider, setBasicSlider] = useState<SliderModels.Slider>()
  const [elements, setElements] = useState<SliderModels.PPTElement[]>([])
  const [pageHeaderElements, setPageHeaderElements] = useState<SliderModels.PPTElement[]>([])
  const [pageRect, setPageRect] = useState<DOMRect>()
  const [chartImage, setChartImage] = useState<string>()

  const titleRef = useRef<HTMLSpanElement>(null)
  const scoreBgRef = useRef<HTMLImageElement>(null)
  const scoreValueRef = useRef<HTMLDivElement>(null)
  const scoreTitleRef = useRef<HTMLDivElement>(null)
  const scoreDescRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<HTMLDivElement>(null)

  const onPageRender = (slider: SliderModels.Slider, pageRect: DOMRect) => {
    setBasicSlider(slider)
    setPageRect(pageRect)
  }
  const onPageHeaderRender = (elements: SliderModels.PPTElement[]) => {
    setPageHeaderElements(elements)
  }

  useEffect(() => {
    if (
      !titleRef.current ||
      !scoreBgRef.current ||
      !scoreValueRef.current ||
      !scoreTitleRef.current ||
      !scoreDescRef.current ||
      !chartRef.current ||
      !chartImage
    ) {
      return
    }

    const titleRect = titleRef.current.getBoundingClientRect()
    const scoreBgRect = scoreBgRef.current.getBoundingClientRect()
    const scoreValueRect = scoreValueRef.current.getBoundingClientRect()
    const scoreTitleRect = scoreTitleRef.current.getBoundingClientRect()
    const scoreDescRect = scoreDescRef.current.getBoundingClientRect()
    const chartRect = chartRef.current.getBoundingClientRect()

    const elements: SliderModels.PPTElement[] = [
      {
        type: 'text',
        content: titleRef.current.outerHTML,
        x: titleRect.x,
        y: titleRect.y - 70,
        width: titleRect.width,
        height: titleRect.height,
      },
      {
        type: 'image',
        src: scoreBgRef.current.src,
        x: scoreBgRect.x,
        y: scoreBgRect.y,
        width: scoreBgRect.width,
        height: scoreBgRect.height,
      },
      {
        type: 'text',
        content: scoreValueRef.current.outerHTML,
        x: scoreValueRect.x,
        y: scoreValueRect.y - 50,
        width: scoreValueRect.width,
        height: scoreValueRect.height,
      },
      {
        type: 'text',
        content: scoreTitleRef.current.outerHTML,
        x: scoreTitleRect.x,
        y: scoreTitleRect.y - 50,
        width: scoreTitleRect.width,
        height: scoreTitleRect.height,
      },
      {
        type: 'text',
        content: scoreDescRef.current.outerHTML,
        x: scoreDescRect.x,
        y: scoreDescRect.y - 50,
        width: scoreDescRect.width,
        height: scoreDescRect.height,
      },
      {
        type: 'image',
        src: chartImage,
        x: chartRect.x,
        y: chartRect.y,
        width: chartRect.width,
        height: chartRect.height,
      },
    ]
    setElements(elements)
  }, [chartImage])

  useEffect(() => {
    if (!pageRect || !basicSlider || pageHeaderElements.length === 0 || elements.length === 0) {
      return
    }
    const _pageHeaderElements = pageHeaderElements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })
    const _elements = elements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })

    const slider: SliderModels.Slider = {
      ...basicSlider,
      elements: [..._pageHeaderElements, ...basicSlider.elements, ..._elements],
    }
    onSliderReady(slider, no)
  }, [pageRect, basicSlider, pageHeaderElements, elements])

  useEffect(() => {
    if (!chartRef.current) {
      return
    }
    html2canvas(chartRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      logging: false,
    }).then((canvas) => {
      const base64Image = canvas.toDataURL('image/png')
      setChartImage(base64Image)
    })
  }, [])

  return (
    <PptPage
      bgSrc={commonUtils.getImagePath('PPTDownload/bg-5.jpg')}
      no={no}
      onPageRender={onPageRender}
    >
      <div className={styles.pptPage6}>
        <PageHeader
          project={project}
          text={'02.' + t('dashboard.projectDetailPage.primaryAnalysis')}
          onRender={onPageHeaderRender}
        />
        <div className={styles.content}>
          <span
            ref={titleRef}
            className={styles.contentTitle}
            style={{
              fontSize: 64,
              fontWeight: 'bold',
              color: '#2A2C3F',
            }}
          >
            {t('dashboard.projectDetailPage.videoView.indexAnalysis')}
          </span>
          <div className={styles.score}>
            <img
              ref={scoreBgRef}
              className={styles.scoreBg}
              style={{ width: 426, height: 178 }}
              src={
                mode === 'high'
                  ? commonUtils.getImagePath('PDFDownload/pdf-page-6/score-bg.png')
                  : commonUtils.getImagePath('PDFDownload/pdf-page-6/score-bg-1.png')
              }
              alt=''
            />
            <div
              className={styles.scoreValue}
              style={{ fontWeight: 'bold', fontSize: 88, color: mode === 'high' ? '#6D54FA' : '#616B7E' }}
              ref={scoreValueRef}
            >
              {project.globalMetrics.overall.score}
            </div>
            <div
              className={styles.scoreTitle}
              style={{ fontSize: 32, color: mode === 'high' ? '#7F69EA' : '#868E9D', fontWeight: 'bold' }}
              ref={scoreTitleRef}
            >
              {t('dashboard.projectDetailPage.videoView.adVideoScore')}
            </div>
            <div
              className={styles.scoreDesc}
              style={{ fontSize: 32, color: '#3E437D', fontWeight: 'bold' }}
              ref={scoreDescRef}
            >
              {t('dashboard.projectDetailPage.videoView.norm')}:{' '}
              {project.globalMetrics.overall.meanScore}
            </div>
          </div>
        </div>
        <div className={styles.chart} ref={chartRef}>
          <VideoScoreBarChart
            chartGap={130}
            overallFlex={1}
            periodFlex={1.8}
            defaultHeight={530}
            barWidth={68}
            barLabelSize={18}
            barTextSize={32}
            barTextLineHeight='51px'
            overallDatas={firstScoreChart.datas}
            periodDatas={secondScoreChart.datas}
          />
        </div>
      </div>
    </PptPage>
  )
}

export default PptPage6
